import React, { useState } from "react";
import { Box, TextField, IconButton, MenuItem, InputAdornment, useTheme, useMediaQuery } from "@mui/material";
import { Call, Close, CallEnd } from "@mui/icons-material";
import { useCallContext } from "../../context/CallContext";
import ChannelSelector from "./ChannelSelector";
import { getChannelById, getChannelComposerSurfaceSx } from "../../utils/utils";
import { useChannels } from "../../hooks/useChannels";

const MessagePhoneChannel = ({
  selectedChannel,
  setSelectedChannel,
  conversationId,
  userPhone = "Seu Telefone", // Telefone do perfil do usuário
  customerPhones = [], // Telefones disponíveis do cliente
  origin,
  setOrigin,
  destination,
  setDestination,
}) => {
  const theme = useTheme();
  const { data: channelsData } = useChannels();
  const channels = channelsData?.channels;
  const currentChannel = getChannelById(channels, selectedChannel);
  const channelName = currentChannel?.nome || currentChannel?.name || null;
  const composerSurface = getChannelComposerSurfaceSx(theme, {
    channelType: "Phone",
    channelName,
    isInternalNote: false,
  });
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [customOrigin, setCustomOrigin] = useState("");
  const [customDestination, setCustomDestination] = useState("");
  const fieldSize = "small";

  const fieldSx = {
    "& .MuiInputBase-root": {
      height: 44,
      borderRadius: 12,
      backgroundColor: theme.palette.background.default,
    },
    "& .MuiInputLabel-root": {
      // deixa o label mais discreto e evita “pular” layout
      fontSize: "0.85rem",
    },
  };

  const { outgoingCall, startOutgoingCall, endOutgoingCall } = useCallContext();

  const handleCall = () => {
    const finalOrigin = origin === "custom" ? customOrigin : origin;
    const finalDestination = destination === "custom" ? customDestination : destination;

    if (finalOrigin && finalDestination) {
      startOutgoingCall({
        channelId: Number(selectedChannel) || null,
        leadId: Number(conversationId) || null,
        fromNumber: finalOrigin === "computer" ? undefined : finalOrigin,
        toNumber: finalDestination,
      });
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        alignItems: "stretch",
        borderRadius: "16px",
        overflow: "hidden",
        minWidth: 0,
        ...composerSurface.shell,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          px: 0.25,
          ...composerSurface.divider,
        }}
      >
        <ChannelSelector integrated selectedChannel={selectedChannel} setSelectedChannel={setSelectedChannel} />
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          gap: 0.75,
          px: 1,
          py: 0.75,
        }}
      >
        {/* Campo para selecionar origem ou digitar manualmente */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {origin !== "custom" ? (
            <TextField
              select
              label="Origem"
              size={fieldSize}
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              fullWidth
              sx={fieldSx}
              SelectProps={{
                MenuProps: {
                  getContentAnchorEl: null,
                  anchorOrigin: { vertical: "top", horizontal: "left" },
                  transformOrigin: { vertical: "bottom", horizontal: "left" },
                },
              }}
            >
              <MenuItem value="computer">Computador</MenuItem>
              <MenuItem value={userPhone}>Meu Telefone ({userPhone})</MenuItem>
              <MenuItem value="custom">Outro</MenuItem>
            </TextField>
          ) : (
            <TextField
              label="Digite o número de origem"
              size={fieldSize}
              value={customOrigin}
              onChange={(e) => setCustomOrigin(e.target.value)}
              fullWidth
              sx={fieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setOrigin("computer")}>
                      <Close />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>

        {/* Campo para selecionar destino ou digitar manualmente */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {destination !== "custom" ? (
            <TextField
              select
              label="Destino"
              size={fieldSize}
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              fullWidth
              sx={fieldSx}
              SelectProps={{
                MenuProps: {
                  getContentAnchorEl: null,
                  anchorOrigin: { vertical: "top", horizontal: "left" },
                  transformOrigin: { vertical: "bottom", horizontal: "left" },
                },
              }}
            >
              {customerPhones.map((phone, index) => (
                <MenuItem key={index} value={phone}>
                  {phone}
                </MenuItem>
              ))}
              <MenuItem value="custom">Outro</MenuItem>
            </TextField>
          ) : (
            <TextField
              label="Digite o número de destino"
              size={fieldSize}
              value={customDestination}
              onChange={(e) => setCustomDestination(e.target.value)}
              fullWidth
              sx={fieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setDestination("")}>
                      <Close />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>

        {/* Botão de Ligar */}
        <IconButton
          onClick={outgoingCall ? endOutgoingCall : handleCall}
          disabled={
            (origin === "custom" && !customOrigin) ||
            (destination === "custom" && !customDestination) ||
            !origin ||
            !destination
          }
          sx={{
            backgroundColor: outgoingCall ? theme.palette.error.main : theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            borderRadius: "50%",
            width: 44,
            height: 44,
            flexShrink: 0,
            "&:hover": {
              backgroundColor: outgoingCall ? theme.palette.error.dark : theme.palette.primary.dark,
            },
          }}
        >
          {outgoingCall ? <CallEnd fontSize="large" /> : <Call fontSize="large" />}
        </IconButton>
      </Box>
    </Box>
  );
};

export default MessagePhoneChannel;
